import { Box, Typography, Tabs, TabList, Tab, Avatar, Sheet, Stack, Chip, CircularProgress } from "@mui/joy";
import { useContext, useEffect, useState } from "react";
import { LayoutContext } from "../context";
import { APIGetLeaderboard, API_URL } from "../api";
import { Trophy, Fire, Star } from "@phosphor-icons/react";


const getLevelName = (level) => {
    if (level <= 5) return 'Novice';
    if (level <= 10) return 'Student';
    if (level <= 20) return 'Resident';
    if (level <= 35) return 'Attending';
    return 'Professor';
};

const getTierColor = (level) => {
    if (level <= 5) return '#6B7280';
    if (level <= 10) return '#3B82F6';
    if (level <= 20) return '#8B5CF6';
    if (level <= 35) return '#F59E0B';
    return '#EAB308';
};

const MEDAL_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32']; // gold, silver, bronze


function PodiumCard({ entry, place, currentUserId }) {
    const isMe = entry.id === currentUserId;
    const medalColor = MEDAL_COLORS[place - 1];
    const isFirst = place === 1;

    return (
        <Sheet
            variant="outlined"
            sx={{
                p: 2,
                borderRadius: 'lg',
                textAlign: 'center',
                flex: 1,
                minWidth: 0,
                position: 'relative',
                order: place === 1 ? 0 : place === 2 ? -1 : 1,
                transform: isFirst ? 'scale(1.05)' : 'none',
                border: isMe ? '2px solid' : undefined,
                borderColor: isMe ? 'primary.500' : undefined,
                background: isMe
                    ? 'var(--joy-palette-primary-softBg)'
                    : undefined,
            }}
        >
            <Box sx={{
                position: 'absolute',
                top: -12,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 28,
                height: 28,
                borderRadius: '50%',
                bgcolor: medalColor,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: place === 1 ? '#7C5800' : '#fff',
                fontWeight: 700,
                fontSize: 13,
                boxShadow: `0 2px 8px ${medalColor}66`,
            }}>
                {place}
            </Box>

            <Avatar
                src={entry.avatar ? `${API_URL}${entry.avatar}` : undefined}
                sx={{
                    width: isFirst ? 64 : 52,
                    height: isFirst ? 64 : 52,
                    mx: 'auto',
                    mt: 1.5,
                    mb: 1,
                    border: `3px solid ${medalColor}`,
                    fontSize: isFirst ? 24 : 20,
                }}
            >
                {entry.first_name?.[0] || '?'}
            </Avatar>

            <Typography
                level="title-sm"
                noWrap
                sx={{ fontWeight: 600 }}
            >
                {entry.first_name} {entry.last_name ? entry.last_name[0] + '.' : ''}
            </Typography>

            <Chip
                size="sm"
                sx={{
                    mt: 0.5,
                    bgcolor: `${getTierColor(entry.level)}22`,
                    color: getTierColor(entry.level),
                    fontWeight: 600,
                    fontSize: 11,
                }}
            >
                LVL {entry.level} {getLevelName(entry.level)}
            </Chip>

            <Typography
                level="title-lg"
                sx={{
                    mt: 1,
                    fontWeight: 700,
                    color: medalColor,
                }}
            >
                {entry.xp_display} XP
            </Typography>

            {entry.streak_current > 0 && (
                <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ mt: 0.5 }}>
                    <Fire size={14} color="#F59E0B" weight="fill" />
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                        {entry.streak_current}
                    </Typography>
                </Stack>
            )}
        </Sheet>
    );
}


function LeaderboardRow({ entry, rank, currentUserId, period }) {
    const isMe = entry.id === currentUserId;
    const xpValue = period === 'weekly' ? entry.weekly_xp : entry.xp_total;

    return (
        <Sheet
            variant={isMe ? 'soft' : 'plain'}
            color={isMe ? 'primary' : undefined}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.2,
                borderRadius: 'md',
                border: isMe ? '1.5px solid' : '1px solid',
                borderColor: isMe ? 'primary.500' : 'divider',
                transition: 'all 0.15s',
                '&:hover': {
                    bgcolor: 'background.level1',
                },
            }}
        >
            {/* Rank */}
            <Typography
                level="body-sm"
                sx={{
                    width: 32,
                    textAlign: 'center',
                    fontWeight: 700,
                    color: isMe ? 'primary.plainColor' : 'text.secondary',
                    fontSize: 14,
                }}
            >
                #{rank}
            </Typography>

            {/* Avatar */}
            <Avatar
                src={entry.avatar ? `${API_URL}${entry.avatar}` : undefined}
                size="sm"
                sx={{ width: 36, height: 36 }}
            >
                {entry.first_name?.[0] || '?'}
            </Avatar>

            {/* Name + level */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography level="body-sm" noWrap sx={{ fontWeight: isMe ? 700 : 500 }}>
                    {entry.first_name} {entry.last_name ? entry.last_name[0] + '.' : ''}
                    {isMe && <Typography component="span" level="body-xs" sx={{ ml: 0.5, color: 'text.tertiary' }}>(Вы)</Typography>}
                </Typography>
            </Box>

            {/* Tier badge */}
            <Chip
                size="sm"
                sx={{
                    bgcolor: `${getTierColor(entry.level)}22`,
                    color: getTierColor(entry.level),
                    fontWeight: 600,
                    fontSize: 11,
                    display: { xs: 'none', sm: 'flex' },
                }}
            >
                {entry.level}
            </Chip>

            {/* Streak */}
            {entry.streak_current > 0 && (
                <Stack direction="row" spacing={0.3} alignItems="center" sx={{ display: { xs: 'none', sm: 'flex' } }}>
                    <Fire size={14} color="#F59E0B" weight="fill" />
                    <Typography level="body-xs">{entry.streak_current}</Typography>
                </Stack>
            )}

            {/* XP */}
            <Typography
                level="body-sm"
                sx={{
                    fontWeight: 700,
                    minWidth: 60,
                    textAlign: 'right',
                    color: isMe ? 'primary.plainColor' : 'text.primary',
                }}
            >
                {xpValue.toLocaleString()} XP
            </Typography>
        </Sheet>
    );
}


export default function LeaderboardPage() {
    const { token, user } = useContext(LayoutContext);
    const [tabIndex, setTabIndex] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const period = tabIndex === 0 ? 'weekly' : 'alltime';

    useEffect(() => {
        if (token) {
            loadLeaderboard();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, token]);

    const loadLeaderboard = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await APIGetLeaderboard(token, period);
            if (response.success) {
                setData(response);
            }
        } catch (err) {
            console.error('Error loading leaderboard:', err);
            setError('Не удалось загрузить рейтинг');
        } finally {
            setLoading(false);
        }
    };

    const leaderboard = data?.leaderboard || [];
    const top3 = leaderboard.slice(0, 3);
    const rest = leaderboard.slice(3);

    // Add xp_display field for podium
    const top3WithDisplay = top3.map(e => ({
        ...e,
        xp_display: period === 'weekly' ? e.weekly_xp : e.xp_total,
    }));

    return (
        <Box sx={{ maxWidth: 640, mx: 'auto', px: { xs: 1, sm: 2 }, py: 3 }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
                <Trophy size={28} color="#F59E0B" />
                <Typography level="h3" sx={{ fontWeight: 700 }}>
                    Рейтинг
                </Typography>
            </Stack>

            {/* Tabs */}
            <Tabs
                value={tabIndex}
                onChange={(e, v) => setTabIndex(v)}
                sx={{ mb: 3, bgcolor: 'transparent' }}
            >
                <TabList
                    variant="outlined"
                    sx={{
                        borderRadius: 'lg',
                        p: 0.5,
                        gap: 0.5,
                        bgcolor: 'background.surface',
                        '--ListItem-radius': '8px',
                    }}
                >
                    <Tab
                        disableIndicator
                        sx={{
                            flex: 1,
                            fontWeight: 600,
                            fontSize: 14,
                            ...(tabIndex === 0 && {
                                bgcolor: 'primary.softBg',
                                color: 'primary.softColor',
                            }),
                        }}
                    >
                        Неделя
                    </Tab>
                    <Tab
                        disableIndicator
                        sx={{
                            flex: 1,
                            fontWeight: 600,
                            fontSize: 14,
                            ...(tabIndex === 1 && {
                                bgcolor: 'primary.softBg',
                                color: 'primary.softColor',
                            }),
                        }}
                    >
                        Все время
                    </Tab>
                </TabList>
            </Tabs>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress size="lg" />
                </Box>
            ) : error ? (
                <Box sx={{ textAlign: 'center', py: 8 }}>
                    <Typography level="title-md" sx={{ color: 'danger.500' }}>
                        {error}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                        Попробуйте обновить страницу
                    </Typography>
                </Box>
            ) : (
                <>
                    {/* Podium Top 3 */}
                    {top3WithDisplay.length > 0 && (
                        <Stack
                            direction="row"
                            spacing={1.5}
                            sx={{
                                mb: 3,
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                                pt: 2,
                            }}
                        >
                            {top3WithDisplay.map((entry, i) => (
                                <PodiumCard
                                    key={entry.id}
                                    entry={entry}
                                    place={i + 1}
                                    currentUserId={user?.id}
                                />
                            ))}
                        </Stack>
                    )}

                    {/* Rest of leaderboard */}
                    {rest.length > 0 && (
                        <Stack spacing={0.75}>
                            {rest.map((entry, i) => (
                                <LeaderboardRow
                                    key={entry.id}
                                    entry={entry}
                                    rank={i + 4}
                                    currentUserId={user?.id}
                                    period={period}
                                />
                            ))}
                        </Stack>
                    )}

                    {/* Current user position (sticky footer) */}
                    {data?.my_rank && (
                        <Sheet
                            variant="soft"
                            color="primary"
                            sx={{
                                mt: 3,
                                p: 2,
                                borderRadius: 'lg',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                border: '1.5px solid',
                                borderColor: 'primary.500',
                            }}
                        >
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Star size={20} color="#F59E0B" weight="fill" />
                                <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                                    Ваша позиция: #{data.my_rank}
                                </Typography>
                            </Stack>
                            <Typography level="title-sm" sx={{ fontWeight: 700 }}>
                                {data.my_xp?.toLocaleString()} XP
                            </Typography>
                        </Sheet>
                    )}

                    {/* Empty state */}
                    {leaderboard.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Trophy size={48} style={{ color: 'var(--joy-palette-text-tertiary)', marginBottom: 16 }} />
                            <Typography level="title-md" sx={{ color: 'text.secondary' }}>
                                {period === 'weekly'
                                    ? 'На этой неделе еще никто не заработал XP'
                                    : 'Пока нет данных'
                                }
                            </Typography>
                            <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                                Отвечайте на вопросы, чтобы попасть в рейтинг!
                            </Typography>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
